import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import CardLoader from '../common/CardLoader';
import { mapInventoryMetrics, mapTopFlavours } from '../../utils/dashboardMappers';
import { formatCurrencyUSD, formatCurrencyCompact, formatPercent, formatDateTimeLocal } from '../../utils/formatters';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import '../pages/DashboardPages.css';
import './Dashboard.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const safeArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const Dashboard = () => {
  // Inventory state
  const [inventory, setInventory] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState(null);

  // Top performers state
  const [topFlavours, setTopFlavours] = useState([]);
  const [topFlavoursLoading, setTopFlavoursLoading] = useState(false);
  const [topFlavoursError, setTopFlavoursError] = useState(null);
  
  // Financial metrics state
  const [financialMetrics, setFinancialMetrics] = useState({
    cogsPerCan: 0,
    wholesaleMargin: 0,
    retailMargin: 0,
    totalRevenue: 0,
    totalInventoryValue: 0,
    // TODO: no backend aggregate for production capacity utilization yet — still a placeholder
    productionCapacity: 0.78
  });
  const [financialMetricsLoading, setFinancialMetricsLoading] = useState(false);
  const [financialMetricsError, setFinancialMetricsError] = useState(null);

  useEffect(() => {
    fetchInventory();
    fetchTopFlavours();
    fetchFinancialMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInventory = async () => {
    setInventoryLoading(true);
    setInventoryError(null);
    try {
      const { data } = await api.get('/api/v1/inventory/?skip=0&limit=50&low_stock_only=false&reorder_only=false');
      setInventory(safeArray(data));
    } catch (err) {
      console.error('Error fetching inventory:', err);
      setInventory([]);
      setInventoryError('Failed to load inventory');
    } finally {
      setInventoryLoading(false);
    }
  };

  const fetchTopFlavours = async () => {
    setTopFlavoursLoading(true);
    setTopFlavoursError(null);
    try {
      const { data } = await api.get('/api/v1/products/analytics/top-performers?metric=revenue&limit=5');
      const mapped = mapTopFlavours(safeArray(data));
      setTopFlavours(mapped);
    } catch (err) {
      console.error('Error fetching top performers:', err);
      setTopFlavours([]);
      setTopFlavoursError('Failed to load top flavours');
    } finally {
      setTopFlavoursLoading(false);
    }
  };
  
  const fetchFinancialMetrics = async () => {
    setFinancialMetricsLoading(true);
    setFinancialMetricsError(null);
    try {
      const { data } = await api.get('/api/v1/financial-analytics/metrics/summary');
      const summary = data?.data || {};
      setFinancialMetrics((prev) => ({
        ...prev,
        cogsPerCan: summary.avg_cogs_per_can ?? 0,
        wholesaleMargin: summary.avg_margin ?? 0,
        retailMargin: summary.avg_retail_margin ?? 0,
        totalRevenue: summary.total_revenue ?? 0,
        totalInventoryValue: summary.total_inventory_value ?? 0
      }));
    } catch (err) {
      console.error('Error fetching financial metrics:', err);
      setFinancialMetricsError('Failed to load financial metrics');
    } finally {
      setFinancialMetricsLoading(false);
    }
  };

  const inventoryMetrics = useMemo(() => mapInventoryMetrics(inventory), [inventory]);

  const productionRisks = (inventoryMetrics?.lowStockCount || 0) + (inventoryMetrics?.needsReorderCount || 0);

  const renderSectionError = (message) => (
    <div className="metric">
      <div className="metric-label">Status</div>
      <div className="metric-value negative" role="alert">{message}</div>
    </div>
  );

  const nameForInventoryItem = (item) => {
    return (
      item?.name ||
      item?.ingredient_name ||
      item?.product_name ||
      item?.sku ||
      `Item #${item?.id ?? '—'}`
    );
  };

  // Chart data for top flavors
  const topFlavoursChartData = {
    labels: topFlavours.map(f => f.flavor),
    datasets: [
      {
        label: 'Revenue',
        data: topFlavours.map(f => f.totalWholesaleRevenue),
        backgroundColor: 'rgba(59, 130, 246, 0.7)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1
      }
    ]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return formatCurrencyUSD(context.raw);
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return formatCurrencyUSD(value);
          }
        }
      }
    }
  };

  // Production data for sparkline
  const productionSparklineData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        data: [65, 72, 78, 75, 82, 78],
        borderColor: productionRisks > 0 ? '#f59e0b' : '#10b981',
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointRadius: 0
      }
    ]
  };

  const sparklineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      }
    },
    scales: {
      x: {
        display: false
      },
      y: {
        display: false,
        min: 50,
        max: 100
      }
    },
    elements: {
      line: {
        tension: 0.4
      }
    }
  };

  return (
    <div className="dashboard-page" data-testid="dashboard-page">
      {/* Financial Metrics Row */}
      <section className="financial-metrics-row" data-testid="financial-metrics">
        <div className="section-header">
          <h3>📊 Key Financial Metrics</h3>
        </div>
        <div className="metrics-cards">
          {financialMetricsLoading ? (
            <CardLoader message="Loading financial metrics..." />
          ) : financialMetricsError ? (
            <div className="error-message">{financialMetricsError}</div>
          ) : (
            <>
              <div className="metric-card">
                <div className="metric-card-content">
                  <div className="metric-card-value">{formatCurrencyUSD(financialMetrics.cogsPerCan)}</div>
                  <div className="metric-card-label">Avg COGS per Can</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-card-content">
                  <div className="metric-card-value">{formatPercent(financialMetrics.wholesaleMargin)}</div>
                  <div className="metric-card-label">Avg Wholesale Margin</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-card-content">
                  <div className="metric-card-value">{formatPercent(financialMetrics.retailMargin)}</div>
                  <div className="metric-card-label">Avg Retail Margin</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-card-content">
                  <div className="metric-card-value">{formatCurrencyCompact(financialMetrics.totalRevenue)}</div>
                  <div className="metric-card-label">Total Revenue (WS)</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-card-content">
                  <div className="metric-card-value">{formatCurrencyCompact(financialMetrics.totalInventoryValue)}</div>
                  <div className="metric-card-label">Total Inventory Value</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-card-content">
                  <div className="metric-card-value">{inventoryMetrics.totalItems}</div>
                  <div className="metric-card-label">Total Items in Stock</div>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-card-content">
                  <div className="metric-card-value">
                    <span className={inventoryMetrics.lowStockCount > 0 ? 'warning-text' : ''}>
                      {inventoryMetrics.lowStockCount}
                    </span>
                  </div>
                  <div className="metric-card-label">Low Stock Count</div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
      
      <div className="dashboard-grid">
        {/* Inventory Overview */}
        <section className="card" data-testid="inventory-overview">
          <div className="card-header">
            <h3><i className="inventory-icon"></i>Inventory Overview</h3>
          </div>
          <div className="card-body">
            {inventoryLoading ? (
              <CardLoader message="Loading inventory..." />
            ) : inventoryError ? (
              renderSectionError(inventoryError)
            ) : (
              <>
                <div className="inventory-summary">
                  <div className="inventory-summary-left">
                    <div className="inventory-stat">
                      <div className="inventory-stat-icon inventory-icon-total"></div>
                      <div className="inventory-stat-content">
                        <div className="inventory-stat-value">{inventoryMetrics.totalItems}</div>
                        <div className="inventory-stat-label">Total Items in Stock</div>
                      </div>
                    </div>
                    <div className="inventory-stat">
                      <div className="inventory-stat-icon inventory-icon-warning"></div>
                      <div className="inventory-stat-content">
                        <div className={`inventory-stat-value ${inventoryMetrics.lowStockCount > 0 ? 'warning-text' : ''}`}>
                          {inventoryMetrics.lowStockCount}
                        </div>
                        <div className="inventory-stat-label">Low Stock Alerts</div>
                      </div>
                    </div>
                    <div className="inventory-stat">
                      <div className="inventory-stat-icon inventory-icon-danger"></div>
                      <div className="inventory-stat-content">
                        <div className={`inventory-stat-value ${inventoryMetrics.needsReorderCount > 0 ? 'danger-text' : ''}`}>
                          {inventoryMetrics.needsReorderCount}
                        </div>
                        <div className="inventory-stat-label">Needs Reorder</div>
                      </div>
                    </div>
                    <div className="inventory-last-updated">
                      <div className="inventory-last-updated-icon"></div>
                      <div className="inventory-last-updated-text">
                        Last updated: {formatDateTimeLocal(inventoryMetrics.lastUpdatedLatest)}
                      </div>
                    </div>
                  </div>
                  
                  {inventoryMetrics.lowestAvailable?.length > 0 && (
                    <div className="inventory-summary-right">
                      <h4 className="inventory-section-title">Lowest Availability</h4>
                      <div className="inventory-table-wrapper">
                        <table className="inventory-table">
                          <thead>
                            <tr>
                              <th>Item</th>
                              <th>Available</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inventoryMetrics.lowestAvailable.map((item) => (
                              <tr key={item?.id ?? nameForInventoryItem(item)}>
                                <td>{nameForInventoryItem(item)}</td>
                                <td>{item?.available_quantity ?? 0}</td>
                                <td>
                                  {item?.needs_reorder ? (
                                    <span className="status-badge danger">Needs Reorder</span>
                                  ) : item?.is_low_stock ? (
                                    <span className="status-badge warning">Low Stock</span>
                                  ) : (
                                    <span className="status-badge success">OK</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </section>


                {/* Top Performing Flavors */}
        <section className="card" data-testid="top-performing-flavors">
          <div className="card-header">
            <h3><i className="flavors-icon"></i>Top Performing Flavors</h3>
          </div>
          <div className="card-body">
            {topFlavoursLoading ? (
              <CardLoader message="Loading top performers..." />
            ) : topFlavoursError ? (
              renderSectionError(topFlavoursError)
            ) : (
              <div className="flavors-content">
                {topFlavours.length > 0 && (
                  <div className="flavors-chart-container">
                    <h4 className="flavors-section-title">Revenue by Flavor</h4>
                    <div className="flavors-chart" style={{ height: '180px' }}>
                      <Bar data={topFlavoursChartData} options={chartOptions} />
                    </div>
                  </div>
                )}
                
                <div className="flavors-table-container">
                  <h4 className="flavors-section-title">Detailed Performance</h4>
                  <div className="flavors-table-wrapper">
                    <table className="flavors-table">
                      <thead>
                        <tr>
                          <th>Flavor</th>
                          <th>Revenue</th>
                          <th>Volume</th>
                          <th>Wholesale Margin</th>
                          <th>Retail Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topFlavours.map((f) => (
                          <tr key={f.flavor}>
                            <td className="flavor-name">{f.flavor}</td>
                            <td className="flavor-revenue">
                              {formatCurrencyUSD(f.totalWholesaleRevenue)}
                              <div className="mini-sparkline">
                                <div className="mini-sparkline-bar" style={{ width: `${Math.min(100, (f.totalWholesaleRevenue / Math.max(...topFlavours.map(item => item.totalWholesaleRevenue))) * 100)}%` }}></div>
                              </div>
                            </td>
                            <td>{f.totalWholesaleVolume}</td>
                            <td>
                              <span className="margin-pill wholesale">{formatPercent(f.wholesaleMargin)}</span>
                            </td>
                            <td>
                              <span className="margin-pill retail">{formatPercent(f.retailMargin)}</span>
                            </td>
                          </tr>
                        ))}
                        {topFlavours.length === 0 && (
                          <tr>
                            <td colSpan={5} style={{ textAlign: 'center' }}>No data</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Production Status */}
        <section className="card" data-testid="production-status">
          <div className="card-header">
            <h3><i className="production-icon"></i>Production Status</h3>
          </div>
          <div className="card-body">
            {inventoryLoading ? (
              <CardLoader message="Loading production indicators..." />
            ) : inventoryError ? (
              renderSectionError('Unavailable')
            ) : (
              <div className="production-status-content">
                <div className="production-status-main">
                  <div className="production-risk-indicator">
                    <div className="production-risk-circle" data-risk={productionRisks > 0 ? 'warning' : 'good'}>
                      <div className="production-risk-value">{productionRisks}</div>
                    </div>
                    <div className="production-risk-label">
                      {productionRisks > 0 ? 'Production Risks' : 'No Production Risks'}
                    </div>
                  </div>
                  
                  <div className="production-sparkline">
                    <div className="production-sparkline-label">Production Capacity Utilization</div>
                    <div className="production-sparkline-chart" style={{ height: '60px' }}>
                      <Line data={productionSparklineData} options={sparklineOptions} />
                    </div>
                    <div className="production-sparkline-value">
                      {formatPercent(financialMetrics.productionCapacity)}
                    </div>
                  </div>
                </div>
                
                <div className="production-status-footer">
                  <div className="production-last-updated">
                    <div className="production-last-updated-icon"></div>
                    <div className="production-last-updated-text">
                      Last inventory update: {formatDateTimeLocal(inventoryMetrics.lastUpdatedLatest)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>


      </div>
    </div>
  );
};

export default Dashboard;